import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Guid,
  isEmptyArray,
  isNil,
  isNotEmptyArray,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControlComponent,
  Icon,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { InputText } from 'primeng/inputtext';
import { SummernoteComponent } from '@web/shared/external';
import { Button } from 'primeng/button';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { Observable, startWith } from 'rxjs';
import {
  AdminBoxCharacteristicFormGroup,
  AdminBoxCharacteristicItem,
  AdminBoxCharacteristics,
  AdminBoxCharacteristicsFormGroup,
} from '../../models/admin-box-details-form';

@Component({
  selector: 'app-admin-box-characteristics-control',
  imports: [
    Accordion,
    AccordionContent,
    AccordionHeader,
    AccordionPanel,
    Button,
    CommonModule,
    FormControlComponent,
    InputText,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    ReactiveFormsModule,
    SummernoteComponent,
  ],
  templateUrl: './admin-box-characteristics-control.component.html',
  styleUrl: './admin-box-characteristics-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminBoxCharacteristicsControlComponent),
      multi: true,
    },
  ],
})
export class AdminBoxCharacteristicsControlComponent
  implements ControlValueAccessor, OnInit
{
  private readonly builder = inject(FormBuilder);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  protected readonly form = this.buildForm();
  protected readonly Icon = Icon;
  protected readonly isNotEmptyArray = isNotEmptyArray;
  protected readonly accordion = viewChild(Accordion);

  protected activeCharacteristicIndex = 0;

  private readonly destroyRef = inject(DestroyRef);

  private onChange!: (value: Nillable<AdminBoxCharacteristics>) => void;
  private onTouched!: () => void;

  public ngOnInit(): void {
    this.listenToFormValueChanges();
  }

  public writeValue(value: Nillable<AdminBoxCharacteristics>): void {
    this.form.controls.characteristics.clear({ emitEvent: false });

    if (isNil(value)) {
      return;
    }

    const { items } = value;

    items.forEach((item) =>
      this.createAndPushNewCharacteristic({ initial: item }),
    );

    this.updateActiveIndex();
  }

  public registerOnChange(
    fn: (value: Nillable<AdminBoxCharacteristics>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  protected handleRemoveClick(index: number): void {
    this.form.controls.characteristics.removeAt(index);
    this.updateActiveIndex();
    this.onTouched();
  }

  protected handleAddClick(): void {
    this.createAndPushNewCharacteristic({ emitEvent: true });
    this.updateActiveIndex();
    this.onTouched();
  }

  protected characteristicsControls(): Array<AdminBoxCharacteristicFormGroup> {
    return this.form.controls.characteristics.controls;
  }

  protected labelForGroup(
    group: AdminBoxCharacteristicFormGroup,
  ): Observable<string> {
    const control = group.controls.label;

    return control.valueChanges.pipe(startWith(control.value));
  }

  private listenToFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        const characteristics = valueOrDefault(value.characteristics, []);

        if (this.form.invalid) {
          this.onChange(null);
          this.onTouched();

          return;
        }

        if (isEmptyArray(characteristics)) {
          this.onChange(null);
          this.onTouched();

          return;
        }

        this.onChange(
          new AdminBoxCharacteristics({
            items: characteristics.map(({ content, label, id }) => ({
              content: valueOrDefault(content, ''),
              id,
              label: valueOrDefault(label, ''),
            })),
          }),
        );
      },
    });
  }

  private buildForm(): AdminBoxCharacteristicsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      characteristics: this.builder.array<AdminBoxCharacteristicFormGroup>([]),
    });
  }

  private createCharacteristicFormGroup(): AdminBoxCharacteristicFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      id: this.builder.control<Nillable<Guid>>(null),
      label: this.builder.nonNullable.control<string>('', [Validators.required]),
      content: this.builder.nonNullable.control<string>('', [Validators.required]),
    });
  }

  private createAndPushNewCharacteristic(
    options: {
      emitEvent?: boolean;
      initial?: AdminBoxCharacteristicItem;
    } = {},
  ): void {
    const { initial, emitEvent = false } = options;
    const formGroup = this.createCharacteristicFormGroup();

    if (!isNil(initial)) {
      formGroup.patchValue(initial);
    }

    this.form.controls.characteristics.push(formGroup, { emitEvent });
  }

  private updateActiveIndex(): void {
    this.activeCharacteristicIndex =
      this.form.controls.characteristics.length - 1;

    this.changeDetectorRef.detectChanges();
  }
}
